import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CATEGORIES, STATUSES } from "@/lib/campaignMeta";
import PhotoManager from "@/components/PhotoManager";
import TagInput from "@/components/TagInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ENGINE_MFRS = [
  "Mercury", "Yamaha", "Suzuki", "Honda Marine", "Tohatsu", "Volvo Penta",
  "Cummins", "Caterpillar", "MAN", "MTU",
];
const OTHER_ENGINE = "Other / Custom";

const EMPTY = {
  year: "", manufacturer: "", model: "", category: "", price: "", original_price: "", promo_price: "",
  engine_manufacturer: "", engine_model: "", engine_count: "", horsepower: "", location: "",
  description: "", key_features: [], listing_url: "", photos: [], status: "In Stock",
  dealer_inventory_date: "", msrp: "", public_pricing_policy: "show_selling",
  floorplan_status: "", floorplan_provider: "", floorplan_funding_date: "", free_floor_end_date: "",
  interest_start_date: "", next_curtailment_date: "", next_curtailment_amount: "",
  current_floorplan_balance: "", current_interest_cost: "", floorplan_data_source: "",
};

const PRICING_POLICIES = [
  { id: "show_selling", label: "Show Selling Price" },
  { id: "msrp_only", label: "MSRP Only" },
  { id: "call_for_price", label: "Call for Price" },
  { id: "hide", label: "Hide Price from Public Marketing" },
];

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};
const fmtDate = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); };

const CURRENT_MODEL_YEAR = new Date().getFullYear();

export default function BoatFormDialog({ open, onOpenChange, boat, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [customEngine, setCustomEngine] = useState(false);
  const [policyTouched, setPolicyTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setPolicyTouched(false);
      const mfr = boat?.engine_manufacturer || "";
      setCustomEngine(!!mfr && !ENGINE_MFRS.includes(mfr));
      setForm(boat ? {
        ...EMPTY, ...boat,
        year: boat.year ?? "", price: boat.price ?? "", horsepower: boat.horsepower ?? "",
        original_price: boat.original_price ?? "", promo_price: boat.promo_price ?? "",
        msrp: boat.msrp ?? "", public_pricing_policy: boat.public_pricing_policy || "show_selling",
        dealer_inventory_date: (boat.dealer_inventory_date || "").slice(0, 10),
        floorplan_status: boat.floorplan_status || "", floorplan_provider: boat.floorplan_provider || "",
        floorplan_funding_date: (boat.floorplan_funding_date || "").slice(0, 10),
        free_floor_end_date: (boat.free_floor_end_date || "").slice(0, 10),
        interest_start_date: (boat.interest_start_date || "").slice(0, 10),
        next_curtailment_date: (boat.next_curtailment_date || "").slice(0, 10),
        next_curtailment_amount: boat.next_curtailment_amount ?? "",
        current_floorplan_balance: boat.current_floorplan_balance ?? "",
        current_interest_cost: boat.current_interest_cost ?? "",
        floorplan_data_source: boat.floorplan_data_source || "",
        key_features: boat.key_features || [], photos: boat.photos || [],
      } : EMPTY);
    }
  }, [open, boat]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setYear = (e) => {
    const year = e.target.value;
    setForm((f) => {
      const next = { ...f, year };
      if (!boat && !policyTouched) {
        const y = parseInt(year, 10);
        next.public_pricing_policy = y && y >= CURRENT_MODEL_YEAR ? "hide" : "show_selling";
      }
      return next;
    });
  };

  const perHp = parseInt(form.horsepower, 10);
  const engCnt = parseInt(String(form.engine_count).replace("+", ""), 10);
  const totalHp = perHp && engCnt ? perHp * engCnt : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      year: form.year ? parseInt(form.year, 10) : null,
      price: form.price ? parseFloat(form.price) : null,
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      promo_price: form.promo_price ? parseFloat(form.promo_price) : null,
      msrp: form.msrp ? parseFloat(form.msrp) : null,
      dealer_inventory_date: form.dealer_inventory_date || null,
      horsepower: form.horsepower ? parseInt(form.horsepower, 10) : null,
      floorplan_status: form.floorplan_status || null,
      floorplan_provider: form.floorplan_provider || null,
      floorplan_funding_date: form.floorplan_funding_date || null,
      free_floor_end_date: form.free_floor_end_date || null,
      interest_start_date: form.interest_start_date || null,
      next_curtailment_date: form.next_curtailment_date || null,
      next_curtailment_amount: form.next_curtailment_amount ? parseFloat(form.next_curtailment_amount) : null,
      current_floorplan_balance: form.current_floorplan_balance ? parseFloat(form.current_floorplan_balance) : null,
      current_interest_cost: form.current_interest_cost ? parseFloat(form.current_interest_cost) : null,
      floorplan_data_source: form.floorplan_data_source || null,
    };
    try {
      if (boat) await api.put(`/boats/${boat.id}`, payload);
      else await api.post("/boats", payload);
      toast.success(boat ? "Boat updated" : "Boat added to inventory");
      onSaved();
    } catch {
      toast.error("Could not save boat");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="boat-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy">{boat ? "Edit Boat" : "Add a Boat"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Year</Label><Input type="number" value={form.year} onChange={setYear} data-testid="boat-year" /></div>
            <div className="space-y-1.5 col-span-1 sm:col-span-2"><Label>Manufacturer</Label><Input required value={form.manufacturer} onChange={set("manufacturer")} data-testid="boat-manufacturer" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Model</Label><Input required value={form.model} onChange={set("model")} data-testid="boat-model" /></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="boat-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="space-y-1.5"><Label>Price ($)</Label><Input type="number" value={form.price} onChange={set("price")} data-testid="boat-price" /></div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="boat-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>HP per Engine</Label>
              <Input type="number" value={form.horsepower} onChange={set("horsepower")} placeholder="e.g. 300" data-testid="boat-hp" />
              {totalHp
                ? <p className="text-[11px] text-gold font-semibold" data-testid="total-hp-hint">= {totalHp.toLocaleString()} total HP ({form.engine_count} × {form.horsepower})</p>
                : <p className="text-[11px] text-muted-foreground">Per single engine - total is calculated</p>}
            </div>
            <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={set("location")} data-testid="boat-location" /></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Date Added to Inventory</Label>
              <Input type="date" value={form.dealer_inventory_date} onChange={set("dealer_inventory_date")} data-testid="boat-dealer-inventory-date" />
              <p className="text-[11px] text-muted-foreground">Enter the date this boat originally entered dealership inventory. RecJet Mind uses this date to calculate inventory age and prioritize marketing opportunities. This is different from the date the record was created in Marine Ad AI. If unknown, leave blank and RecJet Mind will use an estimated age.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Public Pricing Policy</Label>
              <Select value={form.public_pricing_policy} onValueChange={(v) => { setPolicyTouched(true); setForm((f) => ({ ...f, public_pricing_policy: v })); }}>
                <SelectTrigger data-testid="boat-pricing-policy"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRICING_POLICIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Controls what pricing may appear in public marketing. Your internal price is always kept for dealer use and RecJet Mind.</p>
            </div>
          </div>

          {boat && (() => {
            const added = form.dealer_inventory_date || boat.inventory_date || boat.created_at;
            const age = daysSince(added);
            const estimated = !form.dealer_inventory_date;
            return age != null ? (
              <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="boat-inventory-age">
                <span className="font-semibold text-navy">Inventory age: {age} days{estimated ? " (estimated)" : ""}</span>
                <span className="text-muted-foreground"> · Added to inventory: {fmtDate(added)}{estimated ? " (first seen in Marine Ad AI)" : ""}</span>
              </div>
            ) : null;
          })()}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>MSRP ($)</Label>
              <Input type="number" value={form.msrp} onChange={set("msrp")} placeholder="Manufacturer suggested retail" data-testid="boat-msrp" />
              <p className="text-[11px] text-muted-foreground">Shown publicly only when policy is MSRP Only.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Original Price ($)</Label>
              <Input type="number" value={form.original_price} onChange={set("original_price")} placeholder="Price before reduction" data-testid="boat-original-price" />
              <p className="text-[11px] text-muted-foreground">Set this when you drop the advertised price to flag a Price Drop opportunity.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Promotional Price ($)</Label>
              <Input type="number" value={form.promo_price} onChange={set("promo_price")} placeholder="Limited-time offer price" data-testid="boat-promo-price" />
              <p className="text-[11px] text-muted-foreground">Add a promo price to flag a Promotional Offer opportunity.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Engine Manufacturer</Label>
              <SearchableSelect
                value={customEngine ? OTHER_ENGINE : form.engine_manufacturer}
                options={[...ENGINE_MFRS, OTHER_ENGINE]}
                placeholder="Select manufacturer"
                searchPlaceholder="Search manufacturers…"
                testid="boat-engine-mfr"
                onChange={(v) => {
                  if (v === OTHER_ENGINE) {
                    setCustomEngine(true);
                    setForm((f) => ({ ...f, engine_manufacturer: "" }));
                  } else {
                    setCustomEngine(false);
                    setForm((f) => ({ ...f, engine_manufacturer: v }));
                  }
                }}
              />
              {customEngine && (
                <Input
                  value={form.engine_manufacturer}
                  onChange={set("engine_manufacturer")}
                  placeholder="Enter engine manufacturer"
                  className="mt-2"
                  data-testid="boat-engine-mfr-custom"
                />
              )}
            </div>
            <div className="space-y-1.5"><Label>Engine Model</Label><Input value={form.engine_model} onChange={set("engine_model")} data-testid="boat-engine-model" /></div>
            <div className="space-y-1.5">
              <Label>Number of Engines</Label>
              <Select value={form.engine_count} onValueChange={(v) => setForm((f) => ({ ...f, engine_count: v }))}>
                <SelectTrigger data-testid="boat-engine-count"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["1", "2", "3", "4", "5+"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5"><Label>Listing URL</Label><Input value={form.listing_url} onChange={set("listing_url")} placeholder="https://…" data-testid="boat-listing-url" /></div>

          <div className="space-y-1.5"><Label>Short Description</Label><Textarea value={form.description} onChange={set("description")} rows={3} data-testid="boat-description" /></div>

          <div className="space-y-1.5"><Label>Key Features</Label><TagInput tags={form.key_features} onChange={(t) => setForm((f) => ({ ...f, key_features: t }))} placeholder="e.g. Hardtop, Livewell - press Enter" testid="boat-features" /></div>

          <div className="space-y-1.5"><Label>Photos</Label><PhotoManager photos={form.photos} onChange={(p) => setForm((f) => ({ ...f, photos: p }))} testidPrefix="boat-photos" /></div>

          <div className="rounded-lg border border-slate-200 p-4 space-y-3 bg-slate-50/60" data-testid="boat-floorplan-section">
            <div>
              <Label className="text-sm font-semibold text-navy">Floorplan & Carrying Cost (optional)</Label>
              <p className="text-[11px] text-muted-foreground">Unit-level data overrides dealer defaults. Funding date is NOT the date added to inventory. Leave anything you do not know blank; nothing is assumed.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Floorplan Status</Label>
              <div className="flex flex-wrap gap-2">
                {[["financed", "Financed"], ["dealer_owned", "Dealer Owned"], ["unknown", "Unknown"]].map(([val, lbl]) => (
                  <button type="button" key={val} onClick={() => setForm((f) => ({ ...f, floorplan_status: f.floorplan_status === val ? "" : val }))}
                    data-testid={`boat-fp-status-${val}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.floorplan_status === val ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Floorplan Provider / Lender</Label><Input value={form.floorplan_provider} onChange={set("floorplan_provider")} data-testid="boat-fp-provider" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Funding Date</Label><Input type="date" value={form.floorplan_funding_date} onChange={set("floorplan_funding_date")} data-testid="boat-fp-funding" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Free Floor Ends</Label><Input type="date" value={form.free_floor_end_date} onChange={set("free_floor_end_date")} data-testid="boat-fp-freefloor" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Interest Starts</Label><Input type="date" value={form.interest_start_date} onChange={set("interest_start_date")} data-testid="boat-fp-interest" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Next Curtailment</Label><Input type="date" value={form.next_curtailment_date} onChange={set("next_curtailment_date")} data-testid="boat-fp-curtail-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Curtailment Amount</Label><Input type="number" value={form.next_curtailment_amount} onChange={set("next_curtailment_amount")} data-testid="boat-fp-curtail-amt" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Floorplan Balance</Label><Input type="number" value={form.current_floorplan_balance} onChange={set("current_floorplan_balance")} data-testid="boat-fp-balance" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Current Interest Cost</Label><Input type="number" value={form.current_interest_cost} onChange={set("current_interest_cost")} data-testid="boat-fp-interest-cost" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs">Data Source</Label>
                <Select value={form.floorplan_data_source || "none"} onValueChange={(v) => setForm((f) => ({ ...f, floorplan_data_source: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="boat-fp-source"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="lender">Lender (confirmed)</SelectItem>
                    <SelectItem value="dms">DMS (confirmed)</SelectItem>
                    <SelectItem value="accounting">Accounting (confirmed)</SelectItem>
                    <SelectItem value="dealer">Dealer entered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-navy hover:bg-navy-light text-white" data-testid="save-boat-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : boat ? "Save Changes" : "Add Boat"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
